import type { CameraTrackSlot } from "../../interface/webrtc_media_slots";

export type CameraFpsHandler = (fps: number | null) => void;

// 직접제어(direct control) 모드 활성 여부를 팝업에 전달하는 구독 핸들러.
export type ControlModeHandler = (directControlActive: boolean) => void;

// 통신 지연(고RTT) 등 위험 경고 상태 — 콕핏 메인 화면의 붉은 경고 프레임과 동일한
// 경고를 보조 모니터(좌/우)에도 동시에 표시하기 위해 팝업에 broadcast 한다.
export interface ExternalDisplayWarningState {
  active: boolean;
  rttMs: number | null;
}

export type WarningHandler = (warning: ExternalDisplayWarningState) => void;

export const EXTERNAL_DISPLAY_ROLES = [
  "left",
  "right",
] as const;

export type ExternalDisplayRole = typeof EXTERNAL_DISPLAY_ROLES[number];

export type WindowManagementPermissionState =
  | PermissionState
  | "unsupported"
  | "error";

export interface MediaSinkRegistration {
  id?: string;
  slot: CameraTrackSlot;
  role: string;
  windowRef?: Window | null;
  attach: (stream: MediaStream) => void;
  detach: () => void;
  isAvailable?: () => boolean;
}

export interface MultiDisplayLayoutState {
  role: ExternalDisplayRole;
  windowRef: Window | null;
  screenId: string | null;
  permissionState: WindowManagementPermissionState;
  blocked: boolean;
  connected: boolean;
  // 해당 보조 모니터 팝업이 실제로 전체화면(주소창 제거)인지.
  // 팝업이 fullscreenchange 때 opener 의 host.reportFullscreen 으로 보고한다.
  fullscreen: boolean;
}

export type MultiDisplayLayoutStateMap = Record<
  ExternalDisplayRole,
  MultiDisplayLayoutState
>;

// 장치 준비 단계로 전달하는 role 별 요약 상태(열림 + 전체화면 + 창 참조).
// windowRef 는 "해당 창 포커스/raise"(전체화면 클릭 유도) 용도로만 사용.
export interface ExternalDisplayRoleStatus {
  role: ExternalDisplayRole;
  open: boolean;
  fullscreen: boolean;
  windowRef: Window | null;
}

export interface ExternalDisplayHostApi {
  registerSink: (registration: MediaSinkRegistration) => () => void;
  subscribeCameraFps: (
    slot: CameraTrackSlot,
    handler: CameraFpsHandler,
  ) => () => void;
  notifyWindowClosed: (role: ExternalDisplayRole) => void;
  // 보조 모니터 팝업이 자신의 전체화면 상태를 부모(opener)에 보고한다.
  // (장치 준비 단계에서 "좌/우 화면: 열림+전체화면" 게이트 판단에 사용.)
  reportFullscreen: (role: ExternalDisplayRole, isFullscreen: boolean) => void;
  // 직접제어 모드 활성 여부를 구독한다(팝업이 영상 표시 여부를 결정).
  // 구독 즉시 현재 값을 1회 전달하고, 변경 시마다 호출. 해제 함수를 반환.
  subscribeControlMode: (handler: ControlModeHandler) => () => void;
  // 위험 경고 상태(고RTT 등)를 구독한다. 구독 즉시 현재 값을 1회 전달하고 변경 시마다 호출.
  // 보조 모니터가 콕핏과 동일한 붉은 경고 프레임을 표시하는 데 사용. 해제 함수를 반환.
  subscribeWarning: (handler: WarningHandler) => () => void;
}

// 보조 모니터에 "사이드 미러 인셋"으로 표시할 후사경(rear-side) 카메라 슬롯.
// 실제 차량 사이드 미러처럼 후측방 시야를 보여준다.
// (좌 모니터 → 좌후방 카메라, 우 모니터 → 우후방 카메라)
export const EXTERNAL_DISPLAY_SLOT_MAP: Record<
  ExternalDisplayRole,
  CameraTrackSlot
> = {
  left: "left-rear-cam",
  right: "right-rear-cam",
};

// 보조 모니터 창 전체를 채우는 메인 카메라 슬롯(역할별).
// 좌 모니터 → 좌측 카메라, 우 모니터 → 우측 카메라(각 측면 주시야).
// 측면 후사경(좌후방/우후방)은 작은 사이드 미러 인셋으로 겹친다.
// (전방 카메라는 가운데 콕핏 모니터 전용 — 보조 모니터는 측면 시야를 담당.)
export const EXTERNAL_DISPLAY_MAIN_SLOT_MAP: Record<
  ExternalDisplayRole,
  CameraTrackSlot
> = {
  left: "left-cam",
  right: "right-cam",
};

export function createInitialMultiDisplayLayoutState(): MultiDisplayLayoutStateMap {
  return {
    left: {
      role: "left",
      windowRef: null,
      screenId: null,
      permissionState: "unsupported",
      blocked: false,
      connected: false,
      fullscreen: false,
    },
    right: {
      role: "right",
      windowRef: null,
      screenId: null,
      permissionState: "unsupported",
      blocked: false,
      connected: false,
      fullscreen: false,
    },
  };
}
